import React, { useEffect, useState } from 'react';
import { Lock, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissionsRefreshTrigger } from '../contexts/PermissionsContext';

interface Role {
  id: string;
  name: string;
  isDefault: boolean;
  permissions: Record<string, any>;
}

interface ExpandedSections {
  [key: string]: boolean;
}

export default function SettingsRoles() {
  const { user: currentUser } = useAuth();
  const { triggerPermissionsRefresh } = usePermissionsRefreshTrigger();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({});
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userPermissions, setUserPermissions] = useState<Record<string, any>>({});

  useEffect(() => {
    syncAndFetchRoles();
  }, []);

  const syncAndFetchRoles = async () => {
    try {
      setLoading(true);
      // First, sync permissions to ensure all roles have the latest default permissions
      await api.post('/roles/sync-permissions', {});
      // Then fetch the updated roles
      await fetchRoles();
    } catch (err) {
      console.error('Failed to sync permissions:', err);
      // Still try to fetch roles even if sync fails
      await fetchRoles();
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get<any>('/roles');
      setRoles(response.roles || []);

      // Extract current user's permissions from the roles response
      if (response.roles && Array.isArray(response.roles)) {
        const userRole = response.roles.find((r: any) => r.name === currentUser?.role);
        if (userRole && userRole.permissions) {
          const perms = typeof userRole.permissions === 'string'
            ? JSON.parse(userRole.permissions)
            : userRole.permissions;
          setUserPermissions(perms);
        }
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has permission to view and edit roles
  const canEditRoles = userPermissions.roles?.edit === true && userPermissions.roles?.view === true;
  const canViewRoles = userPermissions.roles?.view === true;
  const canEditRole = (roleName: string) => {
    return canEditRoles && roleName !== 'company_owner';
  };

  const handleEditRole = (role: Role) => {
    if (!canEditRole(role.name)) return;
    setEditingRole(role);
    setEditingPermissions(JSON.parse(JSON.stringify(role.permissions)));
    setError('');
    setSuccess('');
  };

  const handleSaveRole = async (closeAfterSave: boolean = false) => {
    if (!editingRole) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.put(`/roles/${editingRole.id}`, {
        permissions: editingPermissions,
      }) as any;

      setRoles((prevRoles) =>
        prevRoles.map((r) =>
          r.id === editingRole.id ? response.role : r
        )
      );

      const roleName = editingRole.name.replace('_', ' ');
      setSuccess(`${roleName} role updated successfully`);

      // If closeAfterSave is true, close the edit screen after saving
      if (closeAfterSave) {
        setEditingRole(null);
      } else {
        // Update the editing role to reflect the response
        setEditingRole(response.role);
      }

      // Refresh permissions in sidebar
      await triggerPermissionsRefresh();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Failed to save role:', err);
      setError(err.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionChange = (
    section: string,
    permission: string,
    value: boolean
  ) => {
    setEditingPermissions((prev) => {
      const updated = {
        ...prev,
        [section]: {
          ...prev[section],
          [permission]: value,
        },
      };

      // If enabling any permission other than 'view', automatically enable 'view'
      if (value && permission !== 'view' && !updated[section].view) {
        updated[section].view = true;
      }

      // If disabling 'view', disable all other permissions in that section
      if (!value && permission === 'view') {
        Object.keys(updated[section]).forEach((perm) => {
          if (perm !== 'view') {
            updated[section][perm] = false;
          }
        });
      }

      return updated;
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Define the sort order for permissions
  const getPermissionSortOrder = (section: string): Record<string, number> => {
    const userPermissionOrder: Record<string, number> = {
      view: 0,
      create: 1,
      edit: 2,
      delete: 3,
      lockUnlock: 4,
      changeRole: 5,
      viewBranches: 6,
      addBranch: 7,
      changeBranch: 8,
      removeBranch: 9,
    };

    const requestPermissionOrder: Record<string, number> = {
      view: 0,
      create: 1,
      edit: 2,
      delete: 3,
      assign: 4,
      add_comment: 5,
    };

    if (section === 'users') {
      return userPermissionOrder;
    }

    if (section === 'requests') {
      return requestPermissionOrder;
    }

    // For other sections, return default order (alphabetical)
    return {};
  };

  // Sort permission entries based on the defined order
  const sortPermissions = (section: string, permissions: Record<string, any>) => {
    const sortOrder = getPermissionSortOrder(section);
    return Object.entries(permissions).sort(([permA], [permB]) => {
      const orderA = sortOrder[permA] ?? 999;
      const orderB = sortOrder[permB] ?? 999;
      return orderA - orderB;
    });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!canViewRoles) {
    return (
      <div className="p-8">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Roles & Permissions</h1>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-blue-800">
              You do not have permission to view role permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
          <p className="text-gray-600 mt-2">
            Configure permissions for each role in your company. The Company Owner role cannot be modified.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">{success}</p>
          </div>
        )}

        {editingRole ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Edit Role Permissions ({editingRole.name.charAt(0).toUpperCase() + editingRole.name.slice(1).replace('_', ' ')})
            </h2>

            <div className="space-y-4">
              {Object.entries(editingPermissions).map(([section, permissions]) => (
                <div key={section} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleSection(section)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-900 capitalize">
                      {section.replace('_', ' ')}
                    </h3>
                    {expandedSections[section] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>

                  {expandedSections[section] && (
                    <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50">
                      {typeof permissions === 'object' &&
                        sortPermissions(section, permissions).map(([perm, value]) => (
                          <label key={perm} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value as boolean}
                              onChange={(e) =>
                                handlePermissionChange(section, perm, e.target.checked)
                              }
                              className="w-4 h-4 rounded border-gray-300 text-cyan-600 cursor-pointer"
                            />
                            <span className="ml-3 text-sm text-gray-700 capitalize">
                              {section === 'users' ? (
                                perm === 'create'
                                  ? 'Add Users'
                                  : perm === 'edit'
                                  ? 'Edit Users'
                                  : perm === 'delete'
                                  ? 'Delete Users'
                                  : perm === 'view'
                                  ? 'View Users'
                                  : perm === 'lockUnlock'
                                  ? 'Lock Unlock'
                                  : perm === 'changeRole'
                                  ? 'Change Role'
                                  : perm === 'viewBranches'
                                  ? 'View Branches'
                                  : perm === 'addBranch'
                                  ? 'Add Branch'
                                  : perm === 'changeBranch'
                                  ? 'Change Branch'
                                  : perm === 'removeBranch'
                                  ? 'Remove Branch'
                                  : perm.replace(/([A-Z])/g, (match) => ` ${match.toLowerCase()}`).trim()
                              ) : section === 'requests' ? (
                                perm === 'add_comment'
                                  ? 'Add Comment'
                                  : perm.replace(/_/g, ' ').replace(/([A-Z])/g, (match) => ` ${match.toLowerCase()}`).trim()
                              ) : (
                                perm.replace(/([A-Z])/g, (match) => ` ${match.toLowerCase()}`).trim()
                              )}
                            </span>
                          </label>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 flex gap-3">
              <button
                onClick={() => handleSaveRole(true)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => handleSaveRole(false)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors font-medium disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Updating...' : 'Update'}
              </button>
              <button
                onClick={() => setEditingRole(null)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {roles.filter(role => role.name !== 'company_owner').map((role) => (
              <div
                key={role.id}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">
                      {role.name.replace('_', ' ')}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {role.isDefault ? 'Default role' : 'Custom role'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {role.name === 'company_owner' && (
                      <div className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded">
                        <Lock className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-600">Protected</span>
                      </div>
                    )}
                    <button
                      onClick={() => handleEditRole(role)}
                      disabled={!canEditRole(role.name)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        canEditRole(role.name)
                          ? 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Edit
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
                    Permissions
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(role.permissions).map(([section, perms]: any) => {
                      const enabledCount = Object.values(perms).filter(Boolean).length;
                      const totalCount = Object.keys(perms).length;
                      return (
                        <div
                          key={section}
                          className="text-sm p-2 bg-gray-50 rounded capitalize"
                        >
                          <div className="font-medium text-gray-700">
                            {section.replace('_', ' ')}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {enabledCount}/{totalCount} enabled
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
